import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CalendarMode } from "../../../shared/types";
import {
  addDays,
  getMonthDays,
  getWeekDays,
} from "../../../shared/types/date-helpers";
import { onNavigateToDate } from "../../../lib/navigation-bus";

export interface CalendarNavigationState {
  readonly currentDate: string;
  readonly selectedDate: string | null;
  readonly calendarMode: CalendarMode;
  readonly weekDays: string[];
  readonly monthDays: string[];
  readonly dayDays: string[];
  readonly goToPrev: () => void;
  readonly goToNext: () => void;
  readonly goToToday: () => void;
  readonly goToDate: (date: string) => void;
  readonly handleDayClick: (date: string) => void;
  readonly setSelectedDate: (date: string | null) => void;
  readonly setCalendarMode: (mode: CalendarMode) => void;
}

export function useCalendarNavigation(today: string): CalendarNavigationState {
  const [currentDate, setCurrentDate] = useState(today);
  const [selectedDate, setSelectedDate] = useState<string | null>(today);
  // Mode par défaut: toujours "week" (les tests E2E s'attendent à ça)
  const [calendarMode, setCalendarMode] = useState<CalendarMode>("week");

  const weekDays = useMemo(() => getWeekDays(currentDate), [currentDate]);
  const monthDays = useMemo(() => getMonthDays(currentDate), [currentDate]);
  const dayDays = useMemo(() => [currentDate], [currentDate]);

  const goToPrev = useCallback(() => {
    const offset = calendarMode === "month" ? -30 : -7;
    const dayOffset = calendarMode === "day" ? -1 : offset;
    setCurrentDate((prev) => addDays(prev, dayOffset));
  }, [calendarMode]);

  const goToNext = useCallback(() => {
    const offset = calendarMode === "month" ? 30 : 7;
    const dayOffset = calendarMode === "day" ? 1 : offset;
    setCurrentDate((prev) => addDays(prev, dayOffset));
  }, [calendarMode]);

  const goToToday = useCallback(() => {
    setCurrentDate(today);
    setSelectedDate(today);
  }, [today]);

  const changeCalendarMode = useCallback(
    (mode: CalendarMode) => {
      setCalendarMode(mode);
      if (mode === "day") setSelectedDate((prev) => prev ?? currentDate);
    },
    [currentDate],
  );

  const handleDayClick = useCallback((date: string) => {
    setSelectedDate(date);
  }, []);

  const goToDate = useCallback((date: string) => {
    setCurrentDate(date);
    setSelectedDate(date);
  }, []);

  // Passage à minuit : mettre à jour le jour courant si on est sur "aujourd'hui"
  const prevTodayRef = useRef(today);
  useEffect(() => {
    if (prevTodayRef.current !== today) {
      const prevToday = prevTodayRef.current;
      prevTodayRef.current = today;
      setCurrentDate((curr) => curr === prevToday ? today : curr);
      setSelectedDate((sel) => sel === prevToday ? today : sel);
    }
  }, [today]);

  // Stable ref so the effect never re-subscribes
  const goToDateRef = useRef(goToDate);

  // Listen for external navigation requests (e.g. from search)
  useEffect(() => {
    goToDateRef.current = goToDate;
    return onNavigateToDate(({ date }) => goToDateRef.current(date));
  }, [goToDate]);

  return {
    currentDate,
    selectedDate,
    calendarMode,
    weekDays,
    monthDays,
    dayDays,
    goToPrev,
    goToNext,
    goToToday,
    goToDate,
    handleDayClick,
    setSelectedDate,
    setCalendarMode: changeCalendarMode,
  };
}
